import React from 'react'
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom'
import ErrorPageComponent from 'components/ErrorPage/ErrorPageComponent'
import Loader from 'components/Loader/Loader'
import errorPageMessages from 'constants/errorPageMessages'
import useAuthHook from 'hooks/use-auth'
import { ErrorPageURLParams } from 'ts/interfaces/ErrorPageComponent.interface'
import Sso from '../SSO/SSO'

interface DecisionEditRouteGuardProps extends RouteProps {
  component: React.ComponentType<any>
}

const DecisionEditRouteGuard: React.FC<DecisionEditRouteGuardProps> = ({
  component: Component,
  ...rest
}) => {
  const location = useLocation<{ fromDecisionPage?: boolean }>()
  const { isAuthenticated, isLoading } = useAuthHook()

  if (isLoading) {
    return <Loader open />
  }

  if (!isAuthenticated && !isLoading) {
    return <Sso />
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        try {
          const { dealNumber, decisionType, decisionReason, emailId } =
            (props.match?.params as ErrorPageURLParams) || {}

          // Validate required parameters
          if (!dealNumber || !decisionType || !decisionReason) {
            return (
              <ErrorPageComponent
                errorCode="MISSING_PATH_PARAMS"
                title="Error"
                description={errorPageMessages.MISSING_PATH_PARAMS}
              />
            )
          }

          if (!emailId) {
            return (
              <ErrorPageComponent
                errorCode="MISSING_EMAIL_ID"
                title="Error"
                description={errorPageMessages.MISSING_EMAIL_ID}
              />
            )
          }

          // Check if user came from Decision page (via Edit button)
          if (location.state?.fromDecisionPage === true) {
            return <Component {...props} />
          }

          // If user manually entered decision edit page URL redirect back to Decision page
          const redirectPath = `/workflow/${dealNumber}/${decisionType}/${decisionReason}/${emailId}`
          return <Redirect to={redirectPath} />
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error in DecisionEditRouteGuard:', error)
          }
          return (
            <ErrorPageComponent
              errorCode="GENERAL_ERROR"
              title="Error"
              description={errorPageMessages.GENERAL_ERROR}
            />
          )
        }
      }}
    />
  )
}

export default DecisionEditRouteGuard
